<template>
  <pn-modal
    label="Edit SAP Price"
    sheet="true"
    max-width="500px"
    :open="open"
    @close="onClose"
  >
    <pn-toast
      v-if="errorMsg"
      temporary
      :text="errorMsg"
      appearance="error"
      @close="errorMsg = ''"
    ></pn-toast>
    <form class="edit-sap-price-form" @submit.prevent="handleUpdate">
      <pn-input
        label="Price"
        name="price"
        type="number"
        :error="priceError"
        v-model="formValues.price"
      ></pn-input>
      <pn-select label="Type" name="type" @change="changeType">
        <option
          :value="PRICE_MANAGEMENT.SAP_PRICE_TYPE.FIXED"
          :selected="formValues.type === PRICE_MANAGEMENT.SAP_PRICE_TYPE.FIXED"
        >
          Fixed
        </option>
        <option
          :value="PRICE_MANAGEMENT.SAP_PRICE_TYPE.UNIT"
          :selected="formValues.type === PRICE_MANAGEMENT.SAP_PRICE_TYPE.UNIT"
        >
          Unit
        </option>
      </pn-select>
      <pn-select label="Status" name="status" @change="changeStatus">
        <option value="Active" :selected="formValues.status === 'Active'">
          Active
        </option>
        <option value="Inactive" :selected="formValues.status === 'Inactive'">
          Inactive
        </option>
        <option value="Draft" :selected="formValues.status === 'Draft'">
          Draft
        </option>
      </pn-select>
      <pn-input
        label="Min Qty"
        name="minQty"
        type="number"
        :error="minQtyError"
        v-model="formValues.minQty"
      ></pn-input>
      <pn-input
        label="Max Qty"
        name="maxQty"
        type="number"
        :error="maxQtyError"
        v-model="formValues.maxQty"
      ></pn-input>
      <pn-date-picker
        label-from="Effective From"
        :start="formValues.effectiveFrom"
        @dateSelection="selectDate($event, 'effectiveFrom')"
      ></pn-date-picker>
      <pn-date-picker
        label-from="Effective To"
        :start="formValues.effectiveTo"
        @dateSelection="selectDate($event, 'effectiveTo')"
      ></pn-date-picker>
      <div class="form-actions">
        <pn-button type="submit" :loading="isLoading">Update</pn-button>
        <pn-button
          appearance="light"
          variant="outlined"
          type="button"
          @click="onClose"
          >Cancel</pn-button
        >
      </div>
    </form>
  </pn-modal>
</template>

<script setup lang="ts">
import { reactive, computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import constant from "@/constant";
import { usePriceManagementStore } from "@/stores/priceManagement";
import type { PnDatePickerSelectEvent } from "@postnord/web-components/types/components";
import type { PriceManagementUpdateSapPriceRequest } from "@/types/priceManagement";

const { PRICE_MANAGEMENT } = constant;

const props = defineProps<{
  open: boolean;
  priceListId: string | null;
  sapPriceDetailId: string | null;
}>();

const emit = defineEmits<{
  (e: "onClose"): void;
  (e: "onClose", payload: { status: "success"; message: string }): void;
}>();

const priceManagementStore = usePriceManagementStore();
const { priceLists, isLoading } = storeToRefs(priceManagementStore);
const errorMsg = ref("");
const showValidationErrors = ref(false);

const selectedSapPriceDetail = computed(() => {
  const pricing = priceLists.value.find(
    (pl) => pl.priceList.id === props.priceListId,
  );
  return pricing?.sapPriceDetails.find(
    (detail) => detail.id === props.sapPriceDetailId,
  );
});

const toDateString = (value: string | null | undefined): string => {
  if (!value) return "";
  return value.slice(0, 10);
};

const formValues = reactive({
  price: "",
  type: PRICE_MANAGEMENT.SAP_PRICE_TYPE.FIXED,
  status: "Draft",
  minQty: "",
  maxQty: "",
  effectiveFrom: "",
  effectiveTo: "",
});

const resetFormValues = () => {
  formValues.price = selectedSapPriceDetail.value?.price ?? "";
  formValues.type =
    selectedSapPriceDetail.value?.priceType ??
    PRICE_MANAGEMENT.SAP_PRICE_TYPE.FIXED;
  formValues.status = selectedSapPriceDetail.value?.status ?? "Draft";
  formValues.minQty = selectedSapPriceDetail.value?.minQty?.toString() ?? "";
  formValues.maxQty = selectedSapPriceDetail.value?.maxQty?.toString() ?? "";
  formValues.effectiveFrom = toDateString(
    selectedSapPriceDetail.value?.effectiveFrom,
  );
  formValues.effectiveTo = toDateString(
    selectedSapPriceDetail.value?.effectiveTo,
  );
};

watch(
  () => props.open,
  (now, prev) => {
    if (now === true && prev !== true) {
      errorMsg.value = "";
      showValidationErrors.value = false;
      resetFormValues();
    }
  },
);

const priceError = computed(() => {
  if (!showValidationErrors.value) return "";
  if (formValues.price === "") {
    return PRICE_MANAGEMENT.SAP_PRICE_REQUIRED_ERROR_MESSAGE;
  }
  const price = Number(formValues.price);
  return Number.isNaN(price) || price <= 0
    ? PRICE_MANAGEMENT.SAP_PRICE_INVALID_ERROR_MESSAGE
    : "";
});

const minQtyError = computed(() => {
  if (!showValidationErrors.value) return "";
  if (
    formValues.type === PRICE_MANAGEMENT.SAP_PRICE_TYPE.UNIT &&
    formValues.minQty === ""
  ) {
    return PRICE_MANAGEMENT.SAP_MIN_QTY_REQUIRED_ERROR_MESSAGE;
  }
  return formValues.minQty !== "" && Number.isNaN(Number(formValues.minQty))
    ? PRICE_MANAGEMENT.SAP_MIN_QTY_INVALID_ERROR_MESSAGE
    : "";
});

const maxQtyError = computed(() => {
  if (!showValidationErrors.value) return "";
  if (
    formValues.type === PRICE_MANAGEMENT.SAP_PRICE_TYPE.UNIT &&
    formValues.maxQty === ""
  ) {
    return PRICE_MANAGEMENT.SAP_MAX_QTY_REQUIRED_ERROR_MESSAGE;
  }
  return formValues.maxQty !== "" && Number.isNaN(Number(formValues.maxQty))
    ? PRICE_MANAGEMENT.SAP_MAX_QTY_INVALID_ERROR_MESSAGE
    : "";
});

const handleUpdate = async () => {
  errorMsg.value = "";
  showValidationErrors.value = true;

  if (!formValues.type || !formValues.effectiveFrom.trim()) {
    errorMsg.value = PRICE_MANAGEMENT.SAP_REQUIRED_FIELDS_ERROR_MESSAGE;
    return;
  }

  if (priceError.value || minQtyError.value || maxQtyError.value) {
    return;
  }

  if (!props.priceListId || !props.sapPriceDetailId) {
    errorMsg.value = "Unable to find selected SAP price detail.";
    return;
  }

  const payload: PriceManagementUpdateSapPriceRequest = {
    priceListId: props.priceListId,
    sapPriceDetailId: props.sapPriceDetailId,
    price: Number(formValues.price),
    type: formValues.type as "fixed" | "unit",
    status: formValues.status,
    ...(formValues.minQty !== "" ? { minQty: Number(formValues.minQty) } : {}),
    ...(formValues.maxQty !== "" ? { maxQty: Number(formValues.maxQty) } : {}),
    effectiveFrom: formValues.effectiveFrom,
    effectiveTo: formValues.effectiveTo,
  };

  const updateResult = await priceManagementStore.updateSapPrice(payload);
  if (updateResult.status === "error") {
    errorMsg.value = updateResult.message;
    return;
  }

  emit("onClose", {
    status: "success",
    message: PRICE_MANAGEMENT.SAP_UPDATE_SUCCESS_MESSAGE,
  });
};

const onClose = () => {
  emit("onClose");
};

const selectDate = (
  event: CustomEvent<PnDatePickerSelectEvent>,
  field: "effectiveFrom" | "effectiveTo",
) => {
  const selectedDate = event.detail.start;
  if (selectedDate) {
    formValues[field] = selectedDate;
  }
};

const changeType = (event: Event) => {
  const target = event.target as HTMLSelectElement;
  formValues.type = target.value;
};

const changeStatus = (event: Event) => {
  const target = event.target as HTMLSelectElement;
  formValues.status = target.value;
};
</script>

<style scoped>
.edit-sap-price-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
}

.edit-sap-price-form :deep(pn-input),
.edit-sap-price-form :deep(pn-select),
.edit-sap-price-form :deep(pn-date-picker) {
  width: 400px;
}
.form-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}
</style>
